/**
 * /w/{tenantSlug} parent layout — owns the sidebar + topbar +
 * ModuleNavProvider + DPDP modal, and resolves the tenant context
 * from the URL slug.
 *
 * Slug-prefix URL refactor (2026-06-03) entrypoint. Replaces the old
 * _dashboard.tsx's body (the auth gate moved up to _app.tsx).
 *
 * Tenant-slug resolution rules:
 *  - URL slug matches /me.tenant.slug → no-op, render the chrome
 *  - URL slug differs from /me.tenant.slug AND the slug is one of the
 *    caller's memberships → trigger /auth/activate to switch, then
 *    re-render
 *  - URL slug is NOT one of the caller's memberships → bounce to
 *    /select-workspace (the backend will 403 every API call anyway;
 *    this is the UX equivalent)
 *
 * The slug is intentionally cosmetic from the backend's perspective —
 * every API call is authenticated by the cookie, and the cookie binds
 * to one tenant at a time. The URL slug only tells the frontend which
 * workspace the user *wanted* to be in; if it doesn't match the
 * cookie's tenant, we either switch (activate) or bounce.
 */

import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Menu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isTotpEnrollmentRequired, toApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BrandLogo } from "@/components/shared/brand-logo";
import { ModuleNavProvider } from "@/components/shared/module-nav";
import { SidebarNav } from "@/components/dashboard/sidebar-nav";
import { TopbarModuleNav } from "@/components/dashboard/topbar-module-nav";
import { UserMenu } from "@/components/dashboard/user-menu";
import { NotificationBell } from "@/components/dashboard/notification-bell";
import { DPDPConsentModal } from "@/components/dashboard/dpdp-consent-modal";
import { EntitySwitcher } from "@/components/shared/entity-switcher";

function WorkspaceNotFound() {
  return (
    <div className="mx-auto max-w-md py-24 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">
        Page not found
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This page doesn&apos;t exist in this workspace.
      </p>
    </div>
  );
}

export const Route = createFileRoute("/_authgated/app/$tenantSlug")({
  component: WorkspaceLayout,
  notFoundComponent: WorkspaceNotFound,
});

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

function WorkspaceLayout() {
  const { user, activate } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug = "" } = useParams({ strict: false }) as {
    tenantSlug?: string;
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  // Resolve URL slug vs the active membership. The _app.tsx parent has
  // already gated unauthenticated + identity-only callers, so user is
  // guaranteed to be a member of SOME tenant by the time we render.
  useEffect(() => {
    if (!user || !tenantSlug) return;
    const activeSlug = user.tenant?.slug;
    if (activeSlug === tenantSlug) return;
    // The URL points at a different workspace than the cookie holds.
    // Try /auth/activate — succeeds if the caller is a member of the
    // URL's workspace, 403s otherwise. activate() rotates the cookie
    // AND calls refresh() under the hood, so /me will reflect the new
    // tenant on the next render.
    let cancelled = false;
    (async () => {
      setActivating(true);
      try {
        await activate(tenantSlug);
      } catch (err) {
        if (cancelled) return;
        // M-1 mandatory-2FA branch — mirrors company-switcher's pattern
        // for parity. The backend has rotated the cookie to enrolment-
        // scoped identity-only at this point, so the only correct move
        // is to force enrolment. Without this branch the user would be
        // silently bounced to /select-workspace with a now-stale `user`
        // state (still tenant-A) while the cookie holds nothing.
        if (isTotpEnrollmentRequired(err)) {
          void navigate({
            to: "/2fa-setup",
            search: { required: 1, next: `/app/${tenantSlug}` },
            replace: true,
          });
          return;
        }
        // Distinguishable error (account_inactive, network 5xx, etc.) —
        // surface the message so the user knows WHY they were bounced.
        // For a plain not_a_member this is a generic toast and the
        // picker explains the rest.
        toast.error(toApiError(err).message);
        void navigate({ to: "/select-workspace", replace: true });
      } finally {
        if (!cancelled) setActivating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, user, activate, navigate]);

  // Either /me hasn't returned yet OR we're mid-activate. Either way,
  // hold the chrome until the slug matches — otherwise the sidebar /
  // topbar would render the OLD workspace's items while the new
  // activate is in flight.
  if (!user || activating || user.tenant?.slug !== tenantSlug) {
    return <FullScreenSpinner />;
  }

  return (
    <ModuleNavProvider>
      <div className="flex min-h-screen bg-gray-50">
        {/* Skip-to-content link (a11y) — first focusable element. On
            tab, it appears in the top-left of the viewport with a high-
            contrast pill; activating it jumps focus past the ~25-link
            sidebar straight to <main id="main-content">. */}
        <a
          href="#main-content"
          className="sr-only z-50 focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to content
        </a>
        {/* Desktop rail */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-border bg-white lg:flex">
          <div className="flex h-16 items-center border-b border-border px-5">
            <Link
              to="/app/$tenantSlug"
              params={{ tenantSlug }}
              aria-label="WorkRight dashboard"
            >
              <BrandLogo />
            </Link>
          </div>
          <ScrollArea className="flex-1">
            <SidebarNav />
          </ScrollArea>
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-white/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:px-6">
            {/* Mobile menu trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Open navigation"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="flex h-16 items-center border-b border-border px-5">
                  <BrandLogo />
                </div>
                <ScrollArea className="h-[calc(100vh-4rem)]">
                  <SidebarNav onNavigate={() => setMobileOpen(false)} />
                </ScrollArea>
              </SheetContent>
            </Sheet>

            <div className="lg:hidden">
              <BrandLogo iconOnly />
            </div>

            {/* Topbar module-nav (lg+ surface; see SOP §1.2) */}
            <TopbarModuleNav />

            <div className="ml-auto flex items-center gap-1 sm:gap-2">
              <EntitySwitcher />
              <NotificationBell />
              <UserMenu />
            </div>
          </header>

          <main id="main-content" className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>

        {/* DPDP Act 2023 per-membership employee-consent capture */}
        <DPDPConsentModal />
      </div>
    </ModuleNavProvider>
  );
}
